/* eslint-disable camelcase */
import { Module } from 'vuex'
import { RootState } from '../types'
import api from '@/api/axios'

export interface Property {
  id: number
  name: string
  address: string
  type: string
  description?: string
  owner_id?: number | null
  owner_name?: string | null
  total_units: number
  occupied_units: number
}

export interface PropertiesState {
  list: Property[]
  current: Property | null
  loading: boolean
}

const properties: Module<PropertiesState, RootState> = {
  namespaced: true,

  state: (): PropertiesState => ({
    list: [],
    current: null,
    loading: false
  }),

  getters: {
    all: (state) => state.list,
    current: (state) => state.current,
    loading: (state) => state.loading
  },

  mutations: {
    SET_LIST (state, list: Property[]) { state.list = list },
    SET_CURRENT (state, property: Property) { state.current = property },
    SET_LOADING (state, val: boolean) { state.loading = val },
    ADD (state, property: Property) { state.list.unshift(property) },
    UPDATE (state, updated: Property) {
      const i = state.list.findIndex(p => p.id === updated.id)
      if (i !== -1) state.list.splice(i, 1, updated)
    },
    REMOVE (state, id: number) {
      state.list = state.list.filter(p => p.id !== id)
    }
  },

  actions: {
    async fetchAll ({ commit }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.get('/properties')
        commit('SET_LIST', data.data ?? data)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchOne ({ commit }, id: number) {
      const { data } = await api.get(`/properties/${id}`)
      commit('SET_CURRENT', data.data ?? data)
    },

    async create ({ commit }, payload: Partial<Property>) {
      const { data } = await api.post('/properties', payload)
      commit('ADD', data.data ?? data)
      return data
    },

    async update ({ commit }, { id, ...payload }: Partial<Property> & { id: number }) {
      const { data } = await api.put(`/properties/${id}`, payload)
      commit('UPDATE', data.data ?? data)
      return data
    },

    async remove ({ commit }, id: number) {
      await api.delete(`/properties/${id}`)
      commit('REMOVE', id)
    }
  }
}

export default properties
